import { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import FixedMenu from '../../../Common/Menus/FixedMenu.jsx';
import { getLayerMoveOptions } from '../../utilities/layerMoveUtils.js';
import { getPhoneRearrangeItems } from '../../utilities/phoneRearrangeUtils.js';
import { EDITOR_MODES } from '../../utilities/simpleEditorMode.js';
import useEscapeToClose from '../../utilities/useEscapeToClose.js';

const PhoneRearrangeMenu = ({
  expanded,
  setExpanded,
  root,
  selectedNodeId,
  editorMode,
  onSelect,
  onReorder,
  onMoveOut,
  onMoveInto,
}) => {
  const [showDestinations, setShowDestinations] = useState(false);
  const [parentId, setParentId] = useState('');
  const [slotId, setSlotId] = useState('');
  const [error, setError] = useState('');
  const selectedRowRef = useRef(null);
  const advancedMode = editorMode === EDITOR_MODES.ADVANCED;
  const items = useMemo(
    () => getPhoneRearrangeItems(root, { editorMode }),
    [editorMode, root]
  );
  const selectedItem = items.find((item) => item.id === selectedNodeId) || null;
  const layerOptions = useMemo(
    () => advancedMode && selectedNodeId ? getLayerMoveOptions(root, selectedNodeId) : null,
    [advancedMode, root, selectedNodeId]
  );
  const selectedParent = layerOptions?.parentCandidates
    .find((candidate) => candidate.id === parentId) || null;
  const selectedSlot = selectedParent?.slots.find((slot) => slot.id === slotId) || null;

  useEscapeToClose(expanded, () => setExpanded(false));

  useEffect(() => {
    setShowDestinations(false);
    setParentId('');
    setSlotId('');
    setError('');
    selectedRowRef.current?.scrollIntoView?.({ block: 'nearest' });
  }, [editorMode, selectedNodeId]);

  useEffect(() => {
    if (!expanded || selectedItem || !items.length) return;
    onSelect(items[0].id);
  }, [expanded, items, onSelect, selectedItem]);

  const moveIntoDestination = () => {
    if (!advancedMode || !selectedNodeId || !selectedParent || !selectedSlot) {
      setError('Choose a destination and position.');
      return;
    }
    if (!onMoveInto(selectedNodeId, selectedParent.id, selectedSlot.id)) {
      setError('That destination is no longer available.');
      return;
    }
    setShowDestinations(false);
    setParentId('');
    setSlotId('');
  };

  return (
    <div className="phone-rearrange-menu">
      <FixedMenu
        className="new-blog-fixed-menu"
        animate
        title="Rearrange Page"
        iconClass="bi-list-ul"
        expanded={expanded}
        setExpanded={setExpanded}
      >
        <div className="phone-rearrange-scroll-content">
          <p className="small text-muted mb-3">
            Choose content, then use the arrow buttons to change its order.
          </p>

          {selectedItem ? (
            <section className="phone-rearrange-selected mb-3" aria-label="Selected content movement">
              <div className="phone-rearrange-selected-copy">
                <span className="small text-muted">Selected</span>
                <strong>{selectedItem.label}</strong>
                {selectedItem.summary ? <span className="small text-truncate">{selectedItem.summary}</span> : null}
              </div>
              {advancedMode ? (
                <div className="phone-rearrange-layer-actions">
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    type="button"
                    disabled={!layerOptions?.canMoveUp}
                    onClick={() => onMoveOut(selectedItem.id)}
                  >
                    <i className="bi bi-box-arrow-up" aria-hidden="true"></i>
                    <span>Move out</span>
                  </button>
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    type="button"
                    disabled={!layerOptions?.parentCandidates.length}
                    onClick={() => setShowDestinations((visible) => !visible)}
                    aria-expanded={showDestinations}
                  >
                    <i className="bi bi-box-arrow-in-down" aria-hidden="true"></i>
                    <span>Move inside</span>
                  </button>
                </div>
              ) : null}
            </section>
          ) : null}

          {advancedMode && showDestinations ? (
            <section className="phone-rearrange-destinations mb-3">
              <h6 className="small fw-bold">Move inside</h6>
              {error ? <div className="alert alert-danger small py-2" role="alert">{error}</div> : null}
              <div className="phone-rearrange-choice-grid mb-2">
                {(layerOptions?.parentCandidates || []).map((candidate) => (
                  <button
                    className={`btn btn-sm text-start ${
                      candidate.id === parentId ? 'btn-primary' : 'btn-outline-secondary'
                    }`}
                    type="button"
                    onClick={() => {
                      setParentId(candidate.id);
                      setSlotId('');
                      setError('');
                    }}
                    aria-pressed={candidate.id === parentId}
                    key={candidate.id}
                  >
                    <i className="bi bi-box me-2" aria-hidden="true"></i>
                    <span className="text-truncate">{candidate.label}</span>
                  </button>
                ))}
              </div>
              {selectedParent ? (
                <div className="phone-rearrange-choice-grid mb-2">
                  {selectedParent.slots.map((slot) => (
                    <button
                      className={`btn btn-sm text-start ${
                        slot.id === slotId ? 'btn-primary' : 'btn-outline-secondary'
                      }`}
                      type="button"
                      onClick={() => {
                        setSlotId(slot.id);
                        setError('');
                      }}
                      aria-pressed={slot.id === slotId}
                      key={slot.id}
                    >
                      {slot.label}
                    </button>
                  ))}
                </div>
              ) : null}
              <button
                className="btn btn-primary btn-sm w-100"
                type="button"
                disabled={!selectedParent || !selectedSlot}
                onClick={moveIntoDestination}
              >
                Move content here
              </button>
            </section>
          ) : null}

          <div className="phone-rearrange-list" aria-label="Page content order">
            {items.map((item) => {
              const selected = item.id === selectedNodeId;
              return (
                <div
                  className={`phone-rearrange-row ${selected ? 'is-selected' : ''}`}
                  data-rearrange-depth={Math.min(item.depth, 5)}
                  ref={selected ? selectedRowRef : null}
                  key={item.id}
                >
                  <button
                    className="phone-rearrange-row-select"
                    type="button"
                    onClick={() => onSelect(item.id)}
                    aria-current={selected ? 'true' : undefined}
                  >
                    <i className={`bi ${selected ? 'bi-record-circle-fill' : 'bi-circle'}`} aria-hidden="true"></i>
                    <span className="phone-rearrange-row-copy">
                      <strong>{item.label}</strong>
                      {item.summary ? <small>{item.summary}</small> : null}
                    </span>
                  </button>
                  <div className="phone-rearrange-row-actions">
                    <button
                      className="btn btn-sm btn-light"
                      type="button"
                      disabled={!item.canMoveUp}
                      onClick={() => onReorder(item.id, -1)}
                      aria-label={`Move ${item.label} up`}
                    >
                      <i className="bi bi-arrow-up" aria-hidden="true"></i>
                    </button>
                    <button
                      className="btn btn-sm btn-light"
                      type="button"
                      disabled={!item.canMoveDown}
                      onClick={() => onReorder(item.id, 1)}
                      aria-label={`Move ${item.label} down`}
                    >
                      <i className="bi bi-arrow-down" aria-hidden="true"></i>
                    </button>
                  </div>
                </div>
              );
            })}
            {!items.length ? (
              <div className="text-center text-muted p-4">There is no content to rearrange yet.</div>
            ) : null}
          </div>
        </div>

        <div className="phone-rearrange-footer">
          <button className="btn btn-primary w-100" type="button" onClick={() => setExpanded(false)}>
            Done
          </button>
        </div>
      </FixedMenu>
    </div>
  );
};

PhoneRearrangeMenu.propTypes = {
  expanded: PropTypes.bool.isRequired,
  setExpanded: PropTypes.func.isRequired,
  root: PropTypes.object,
  selectedNodeId: PropTypes.string,
  editorMode: PropTypes.oneOf(Object.values(EDITOR_MODES)).isRequired,
  onSelect: PropTypes.func.isRequired,
  onReorder: PropTypes.func.isRequired,
  onMoveOut: PropTypes.func.isRequired,
  onMoveInto: PropTypes.func.isRequired,
};

PhoneRearrangeMenu.defaultProps = {
  root: null,
  selectedNodeId: null,
};

export default PhoneRearrangeMenu;
